import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  Box,
} from '@mui/material';
import { useDebounced } from '@/hook/useDebounced';

/**
 * Generic filter bar
 *
 * Props:
 * - config: Array<{
 *     type: 'select' | 'text',
 *     name: string,
 *     label: string,
 *     options?: Array<{value:string, label:string}>
 *     placeholder?: string,
 *     grid?: { xs?: number, sm?: number, md?: number },
 *   }>
 * - filters: object
 * - setFilters: (updater) => void
 * - debounceDelay?: number
 * - syncWithUrl?: boolean
 * - showReset?: boolean
 * - resetLabel?: string
 */
const GenericFilter = ({
  config = [],
  filters,
  setFilters,
  debounceDelay = 500,
  syncWithUrl = true,
  showReset = true,
  resetLabel = 'Reset',
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Collect text field names
  const textFieldKeys = useMemo(
    () => config.filter(field => field.type === 'text').map(field => field.name),
    [config]
  );

  // Initialize text input state from filters
  const initialTextInputValues = useMemo(() => {
    const values = {};
    textFieldKeys.forEach(key => {
      values[key] = filters?.[key] ?? '';
    });
    return values;
  }, [filters, textFieldKeys]);

  const [textInputValues, setTextInputValues] = useState(initialTextInputValues);

  // Sync filters from URL on mount
  useEffect(() => {
    if (!syncWithUrl) return;

    const urlFilters = {};
    config.forEach(({ name }) => {
      urlFilters[name] = searchParams.get(name) ?? '';
    });

    const hasDifference = Object.keys(urlFilters).some(
      key => String(urlFilters[key] ?? '') !== String(filters?.[key] ?? '')
    );

    if (hasDifference) {
      setFilters(prev => ({ ...prev, ...urlFilters }));
    }

    // Hydrate text state from URL
    setTextInputValues(prev => {
      const next = { ...prev };
      textFieldKeys.forEach(key => {
        next[key] = urlFilters[key] ?? '';
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 


  const debouncedTextInputValues = useDebounced(textInputValues, debounceDelay);

  // Apply debounced text input values into external filters
  useEffect(() => {
    setFilters(prev => {
      const next = { ...prev };
      textFieldKeys.forEach(key => {
        next[key] = debouncedTextInputValues[key] ?? '';
      });
      return next;
    });
  }, [debouncedTextInputValues, setFilters, textFieldKeys]);

  // Sync filters → URL
  useEffect(() => {
    if (!syncWithUrl) return;

    const params = new URLSearchParams();
    config.forEach(({ name }) => {
      const value = filters?.[name];
      if (value) params.set(name, value);
    });
    setSearchParams(params, { replace: true });
  }, [filters, config, setSearchParams, syncWithUrl]);

  const handleSelectChange = (fieldName) => (event) => {
    const value = event.target.value;
    setFilters(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleTextChange = (fieldName) => (event) => {
    const value = event.target.value;
    setTextInputValues(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleResetFilters = () => {
    const clearedFilters = {};
    config.forEach(({ name }) => { clearedFilters[name] = ''; });
    setFilters(clearedFilters);

    setTextInputValues(prev => {
      const next = { ...prev };
      textFieldKeys.forEach(key => { next[key] = ''; });
      return next;
    });
  };

  const getGridProps = (grid) => ({
    xs: grid?.xs ?? 12,
    sm: grid?.sm ?? 3,
    md: grid?.md,
  });

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2} alignItems="end">
        {config.map((field) => {
          const { type, name, label, options = [], placeholder, grid } = field;
          const gridProps = getGridProps(grid);
          const value =
            type === 'text'
              ? (textInputValues?.[name] ?? '')
              : (filters?.[name] ?? '');

          return (
            <Grid item key={name} {...gridProps}>
              {type === 'select' ? (
                <FormControl fullWidth size="small">
                  <InputLabel>{label}</InputLabel>
                  <Select
                    value={value}
                    label={label}
                    onChange={handleSelectChange(name)}
                  >
                    {options.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label={label}
                  value={value}
                  onChange={handleTextChange(name)}
                  placeholder={placeholder}
                />
              )}
            </Grid>
          );
        })}

        {showReset && (
          <Grid item xs={12} sm={2}>
            <Button
              variant="outlined"
              fullWidth
              size="small"
              onClick={handleResetFilters}
              sx={{ height: '2.23rem' }}
            >
              {resetLabel}
            </Button>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default GenericFilter;
